/* global logger */

import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { detect } from 'detect-browser';
import { useLocation } from 'react-router-dom';
import componentComposer from '../../core/componentComposer';
import {
    isClient,
    parseUrl,
    setCookie,
    dataLayer as DataLayer,
    currentLocale,
    miniGazelle,
    getUrl,
} from '../../helpers';
import GeoLocation from '../../components/modules/GeoLocation/GeoLocation';
import DynamicSchemaMarkup from '../../components/shared/DynamicSchemaMarkup/DynamicSchemaMarkup';

const ComponentTree = (props = {}) => {
    const { page = {}, navigation = {}, location, httpStatus = 500, components = {}, labels = {}, loading } =
        props || {};

    const { modules = [], metadata = {}, requestRelated = {}, dataLayer = {} } = page || {};

    const { navigation: navigationData = {}, footer: footerData = {} } = navigation || {};

    const {
        seoTitle = '',
        seoDescription = '',
        seoIndexable,
        seoTags = '',
        type = '',
        logicalName = '',
        section = '',
        thumbnail = '',
    } = metadata || {};

    const { pathname = '' } = location;
    const [metaRemoved, setMetaRemoved] = useState(1);
    const locationInfo = useLocation();

    useEffect(() => {
        if (!basePath.includes('localhost')) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.innerHTML = `(function(a,b,c,d){ a='https://tags.tiqcdn.com/utag/sony-marketing/sony-pro/${tealiumEnv}/utag.js';
            b=document;c='script';d=b.createElement(c);d.src=a;d.type='text/java'+c;d.async=true;
            a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a); })();`;

            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
    }, []);

    /**
     * Observes the <head> for changes to meta tags.
     * If meta tags are removed (e.g., by a third-party script),
     * this will restore them using react-helmet.
     */
    useEffect(() => {
        const observer = new MutationObserver(mutations => {
            const headChanged = mutations.some(mutation =>
                Array.from(mutation.removedNodes).some(removedNode => {
                    return removedNode.nodeName === 'META' && removedNode.getAttribute?.('name');
                })
            );

            if (headChanged) {
                setMetaRemoved(prev => prev + 1);
            }
        });

        observer.observe(document.head, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    /**
     * Set labels
     */

    miniGazelle.setLabels(labels);

    /**
     * product can be null type so guard against
     */

    const product = dataLayer?.product ? dataLayer?.product : {};
    const { retired } = product;

    const urlParser = new parseUrl();

    let localisationData = {
        language: dataLayer?.language ? dataLayer?.language : 'en',
        country: dataLayer?.country ? dataLayer?.country : 'EU',
        currentPath: urlParser.parse(pathname).raw.parsed,
    };

    localisationData.locale = `${localisationData.language}_${localisationData.country}`;

    /**
     * Set the re-useable locale data
     */

    currentLocale.set(localisationData.locale);

    /**
     * Set the browser cookie
     */

    setCookie({
        locale: localisationData.locale,
        currentPath: localisationData.currentPath,
    });

    /**
     * Load any optional extra styles or scripts depending on thw browser
     */

    const browser = detect() || {};

    const browserName = browser.name ? browser.name : 'ie';

    logger.info('src.containers.ComponentTree.js: Browser: %s', browserName);

    let scriptExtras = [];
    let styleExtras = [];

    switch (browserName) {
        case 'ie': {
            scriptExtras = [
                // {
                //     src: 'https://oss.maxcdn.com/html5shiv/3.7.3/html5shiv.min.js',
                //     type: 'text/javascript'
                // },
                // {
                //     src: 'https://oss.maxcdn.com/respond/1.4.2/respond.min.js',
                //     type: 'text/javascript'
                // }
            ];
            styleExtras = [
                {
                    rel: 'stylesheet',
                    type: 'text/css',
                    href: '/api/public/assets/styles/overrides/ngp-ie.css',
                },
            ];
            break;
        }

        case 'edge': {
            styleExtras = [
                {
                    rel: 'stylesheet',
                    type: 'text/css',
                    href: '/api/public/assets/styles/overrides/ngp-ie.css',
                },
            ];
            break;
        }

        default: {
            styleExtras = [];
            scriptExtras = [];
        }
    }

    /**
     * Fire data layer events
     */

    /**
     * Patch referrer
     */

    const referrer = '';

    if (isClient()) {
        DataLayer.prepareCustomEvent();
        DataLayer.update(metadata, modules, httpStatus, location, referrer, dataLayer, requestRelated);
    }

    /**
     * Compose components
     */

    const extraProps = {
        localisationData,
        labels,
        location,
        dataLayer,
        locale: localisationData.locale,
        browserName,
        metadata,
        setPurchaseData: () => { },
        purchaseData: {},
        loading,
    };

    logger.info('src.containers.ComponentTree.js: Composing components');

    const ComposedComponents = componentComposer({
        modules,
        extraProps,
        UiComponents: components,
    });

    const noIndex = (
        <Helmet
            meta={[
                {
                    name: 'robots',
                    content: 'noindex, nofollow',
                },
            ]}
        />
    );

    logger.info('src.containers.ComponentTree.js: Rendering...');

    const contentData = () => {
        let sectionKey = section;

        if ((section === "products" || section === "solutions") && modules?.some(({ name }) => name === 'Breadcrumb')) {
            const breadcrumbData = modules.find(({ name }) => name === 'Breadcrumb');
            const structure = breadcrumbData?.data?.structure;

            if (structure?.length >= 2) {
                const category = structure[structure.length - 2];
                sectionKey = `${section}-${category?.name}`;
            }
        }

        if (dataLayer?.template === "press" && !section && section === "") {
            sectionKey = "press-release"
        }

        return { sectionKey };
    };

    const basePath = getUrl();
    const environments = ['preview', 'localhost', 'dev', 'stg'];
    const tealiumEnv = environments.some(env => basePath.includes(env)) ? 'qa' : 'prod';
    const organization = { name: 'Sony', url: basePath + '/' + localisationData.locale, logo: basePath + '/assets/static/media/Sony_Logo.svg' };
    const isProduct = dataLayer?.product;
    const productDetails = isProduct
        ? [
            {
                name: dataLayer?.product?.catalogName,
                image: thumbnail,
                description: seoDescription,
                brand: 'Sony',
                model: dataLayer?.product?.materialName,
            }
        ] : null;

    const isCaseStudy = dataLayer?.pageType === "Case Studies";
    const relatedProductsDetails = isCaseStudy ? modules
        .filter((module) => module.name === "RelatedAssets")
        .flatMap((ele) =>
            ele.data.related?.map((relatedProduct) => ({
                name: relatedProduct.title,
                image: relatedProduct.image,
                description: relatedProduct.intro,
                brand: 'Sony',
            }))
        ) : null;
    const article = isCaseStudy ? {
        pageUrl: basePath + locationInfo.pathname,
        headline: metadata?.seoTitle,
        inLanguage: `${dataLayer?.language}-${dataLayer?.country}`,
        description: metadata?.seoDescription,
        keywords: metadata?.seoTags.split(',').map((ele) => ele.trim()),
    } : null;
    const productInfo = isCaseStudy ? relatedProductsDetails : productDetails;

    const { sectionKey } = contentData();

    const meta = [
        {
            name: 'description',
            content: seoDescription,
        },
        {
            name: 'keywords',
            content: seoTags,
        },
        {
            name: 'locale',
            content: localisationData.locale,
        },
        {
            name: 'logicalPage',
            content: type ? type : 'default',
        },
        {
            name: 'assetId',
            content: logicalName || (dataLayer?.template === "press" ? "article" : "undefined-logical-name"),
        },
        {
            name: 'section',
            content: sectionKey,
        },
        {
            name: 'thumbnail',
            content: thumbnail,
        },
        {
            property: 'og:image',
            content: thumbnail,
        },
        {
            property: 'og:title',
            content: seoTitle,
        },
        {
            property: 'retired',
            content: retired,
        }
    ];

    if (section === "products") {
        meta.push({
            name: 'og:description',
            content: seoDescription,
        })
    } else if (section === "solutions") {
        meta.push({
            property: 'og:description',
            content: seoDescription,
        })
    }

    return (
        <Fragment>
            <Helmet
                key={`react_helmet_${metaRemoved}`}
                htmlAttributes={{
                    lang: localisationData.language,
                }}
                title={seoTitle ? seoTitle : 'Sony Professional'}
                defaultTitle={'Sony Professional'}
                meta={meta}
                script={[
                    {
                        src: 'https://jwpsrv.com/library/6qcWCLNWEeS4Ig4AfQhyIQ.js',
                        type: 'text/javascript',
                    },
                    {
                        src: `https://tags.tiqcdn.com/utag/sony-marketing/sony-pro/${tealiumEnv}/utag.sync.js`,
                        type: 'text/javascript',
                    },
                    {
                        type: 'text/javascript',
                        innerHTML: 'var utag_data = { website_platform : "ngp" };',
                    },
                    ...scriptExtras,
                ]}
                link={styleExtras}
            />
            {isClient() && <GeoLocation />}
            {navigationData && <components.Navigation {...navigationData} />}
            {seoIndexable !== undefined ? (seoIndexable ? null : noIndex) : null}
            <main className="composed-components tw-font-sst-light">{ComposedComponents}</main>
            {/* footer */}
            {footerData && (
                <components.Footer
                    {...footerData}
                    locale={localisationData.locale}
                    countries={metadata && metadata.countries ? metadata.countries : null}
                />
            )}
            {(dataLayer?.template === "press" || ((isCaseStudy || isProduct) && productInfo)) && (
                <DynamicSchemaMarkup
                    products={productInfo}
                    organization={organization}
                    article={article}
                />
            )}
        </Fragment>
    );
};

ComponentTree.propTypes = {
    httpStatus: PropTypes.number,
    /**
     * components
     */

    components: PropTypes.object.isRequired,

    /**
     * location
     */

    location: PropTypes.object.isRequired,

    /**
     * page
     */

    page: PropTypes.shape({
        metadata: PropTypes.object,
        modules: PropTypes.array,
        requestRelated: PropTypes.object,
        dataLayer: PropTypes.object,
        httpStatus: PropTypes.number,
    }).isRequired,

    /**
     * navigation
     */

    navigation: PropTypes.shape({
        navigation: PropTypes.object.isRequired,
        footer: PropTypes.object.isRequired,
        copyright: PropTypes.object.isRequired,
        country: PropTypes.object.isRequired,
    }).isRequired,

    /**
     * labels
     */

    labels: PropTypes.object,
};

export default ComponentTree;
